import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../../components/Layout';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Site {
  id: number;
  accountCode: string;
  siteName: string;
  address?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  notes?: string;
  isActive: boolean;
  zones?: Zone[];
  contacts?: Contact[];
}

interface Zone {
  id: number;
  siteId: number;
  zoneNumber: number;
  zoneLabel?: string;
  area?: string;
  zoneType: 'motion' | 'door' | 'panic' | 'fire' | 'tamper' | 'duress' | 'other';
  severity: 'critical' | 'high' | 'medium' | 'low';
  isActive: boolean;
  notes?: string;
}

interface Contact {
  id: number;
  siteId: number;
  name: string;
  role: 'keyholder' | 'manager' | 'emergency' | 'police' | 'fire' | 'other';
  phone?: string;
  email?: string;
  priorityOrder: number;
  isActive: boolean;
  notes?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const ZONE_TYPE_LABELS: Record<Zone['zoneType'], string> = {
  motion: 'Motion/PIR',
  door: 'Door Contact',
  panic: 'Panic/Duress',
  fire: 'Fire/Smoke',
  tamper: 'Tamper',
  duress: 'Duress Button',
  other: 'Other',
};

const ZONE_SEVERITY_COLORS: Record<Zone['severity'], string> = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#eab308',
  low: '#22c55e',
};

const CONTACT_ROLE_LABELS: Record<Contact['role'], string> = {
  keyholder: 'Key Holder',
  manager: 'Site Manager',
  emergency: 'Emergency',
  police: 'Police',
  fire: 'Fire Brigade',
  other: 'Other',
};

const CONTACT_ROLE_COLORS: Record<Contact['role'], string> = {
  keyholder: '#3b82f6',
  manager: '#a855f7',
  emergency: '#ef4444',
  police: '#1d4ed8',
  fire: '#f97316',
  other: '#555555',
};

// ─── Auth helper ──────────────────────────────────────────────────────────────

const authHeaders = (): Record<string, string> => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('lemai_token') || ''}`,
});

// ─── Shared style constants ───────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.1)',
  color: '#e5e5e5',
  padding: '8px 10px',
  borderRadius: '6px',
  fontSize: '12px',
  width: '100%',
  outline: 'none',
  boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  background: 'rgba(255,255,255,0.08)',
  cursor: 'pointer',
};

const btnPrimary: React.CSSProperties = {
  background: '#3b82f6',
  color: 'white',
  border: 'none',
  padding: '8px 14px',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: 600,
};

const btnSecondary: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  color: '#a0a0a0',
  border: '1px solid rgba(255,255,255,0.1)',
  padding: '7px 12px',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '12px',
};

const btnDanger: React.CSSProperties = {
  background: 'rgba(239,68,68,0.15)',
  color: '#ef4444',
  border: '1px solid rgba(239,68,68,0.2)',
  padding: '6px 10px',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '11px',
};

// ─── Toast ────────────────────────────────────────────────────────────────────

interface ToastMsg { id: number; msg: string; type: 'error' | 'success' | 'info' }

function ToastArea({ toasts, onDismiss }: { toasts: ToastMsg[]; onDismiss: (id: number) => void }) {
  if (toasts.length === 0) return null;
  return (
    <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999, display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '360px' }}>
      {toasts.map(t => (
        <div key={t.id} onClick={() => onDismiss(t.id)} style={{
          background: t.type === 'error' ? 'rgba(239,68,68,0.18)' : t.type === 'success' ? 'rgba(34,197,94,0.18)' : 'rgba(59,130,246,0.18)',
          border: `1px solid ${t.type === 'error' ? 'rgba(239,68,68,0.4)' : t.type === 'success' ? 'rgba(34,197,94,0.4)' : 'rgba(59,130,246,0.4)'}`,
          color: t.type === 'error' ? '#f87171' : t.type === 'success' ? '#4ade80' : '#60a5fa',
          padding: '10px 14px', borderRadius: '4px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
        }}>
          {t.msg}
        </div>
      ))}
    </div>
  );
}

// ─── Spinner ──────────────────────────────────────────────────────────────────

function Spinner({ size = 18 }: { size?: number }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      border: `2px solid rgba(255,255,255,0.1)`,
      borderTopColor: '#3b82f6',
      animation: 'sp-spin 0.7s linear infinite',
      display: 'inline-block',
    }} />
  );
}

// ─── Toggle ───────────────────────────────────────────────────────────────────

function Toggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div onClick={() => onChange(!value)} style={{
      width: '32px', height: '18px', borderRadius: '9px',
      background: value ? '#22c55e' : '#222222',
      cursor: 'pointer', position: 'relative', flexShrink: 0, transition: 'background 0.2s',
    }}>
      <div style={{
        position: 'absolute', top: '2px',
        left: value ? '16px' : '2px',
        width: '14px', height: '14px', borderRadius: '50%',
        background: 'white', transition: 'left 0.2s',
      }} />
    </div>
  );
}

// ─── CSV Column Mapping Modal ─────────────────────────────────────────────────

interface CsvMappingModalProps {
  siteId: number;
  file: File;
  onDone: (result: { imported: number; updated: number; skipped: number; errors: string[]; zones: Zone[] }) => void;
  onCancel: () => void;
  onError: (msg: string) => void;
}

const CSV_FIELDS: { key: string; label: string; required: boolean; hint: string }[] = [
  { key: 'zone_number', label: 'Reporting ID', required: true,  hint: 'The CID Reporting ID the panel sends on alarm (e.g. Reporting ID column)' },
  { key: 'zone_label',  label: 'Zone Label',  required: false, hint: 'Human-readable zone name / description' },
  { key: 'area',        label: 'Area',        required: false, hint: 'Area or partition name (e.g. Area 1)' },
  { key: 'zone_type',   label: 'Zone Type',   required: false, hint: 'Input type column (auto-converted to door/motion/etc.)' },
  { key: 'severity',    label: 'Severity',    required: false, hint: 'Alarm severity: critical/high/medium/low' },
  { key: 'is_active',   label: 'Active',      required: false, hint: 'Whether zone is active (true/false)' },
  { key: 'notes',       label: 'Notes',       required: false, hint: 'Notes / comments column' },
];

function parseCsvHeadersFromText(text: string): { headers: string[]; preview: string[][] } {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(l => l.trim());
  if (lines.length === 0) return { headers: [], preview: [] };

  function parseLine(line: string): string[] {
    const result: string[] = [];
    let cur = '';
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
        else inQ = !inQ;
      } else if (c === ',' && !inQ) {
        result.push(cur.trim()); cur = '';
      } else cur += c;
    }
    result.push(cur.trim());
    return result;
  }

  const rawH = parseLine(lines[0]);
  const seen: Record<string, number> = {};
  const headers = rawH.map(h => {
    const clean = h.replace(/^"|"$/g, '').trim();
    if (seen[clean] === undefined) { seen[clean] = 0; return clean; }
    seen[clean]++; return `${clean}_${seen[clean] + 1}`;
  });

  const preview = lines.slice(1, 4).map(l =>
    parseLine(l).map(v => v.replace(/^"|"$/g, '').slice(0, 40))
  );
  return { headers, preview };
}

function autoDetectMapping(headers: string[]): Record<string, string> {
  const map: Record<string, string> = {};
  const hints: Record<string, string[]> = {
    zone_number: ['reporting id', 'zone_number', 'zone number', 'zone #', 'zone no', 'number', 'num', 'reporting_id', 'cid zone'],
    zone_label:  ['name', 'zone_label', 'zone label', 'label', 'description', 'zone name', 'input name'],
    area:        ['area 1', 'area', 'area_1', 'partition', 'section', 'alarm area'],
    zone_type:   ['input type 1', 'zone_type', 'zone type', 'type', 'detector type', 'input type'],
    severity:    ['severity', 'priority'],
    is_active:   ['active', 'is_active', 'enabled'],
    notes:       ['notes', 'note', 'comment'],
  };
  const used = new Set<string>();
  for (const [field, candidates] of Object.entries(hints)) {
    for (const c of candidates) {
      const match = headers.find(h => h.toLowerCase() === c && !used.has(h));
      if (match) { map[field] = match; used.add(match); break; }
    }
  }
  return map;
}

function CsvMappingModal({ siteId, file, onDone, onCancel, onError }: CsvMappingModalProps) {
  const [headers, setHeaders] = useState<string[]>([]);
  const [preview, setPreview] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    const reader = new FileReader();
    reader.onload = e => {
      const text = (e.target?.result as string) || '';
      const { headers: h, preview: p } = parseCsvHeadersFromText(text);
      setHeaders(h);
      setPreview(p);
      setMapping(autoDetectMapping(h));
      setLoading(false);
    };
    reader.readAsText(file);
  }, [file]);

  async function handleImport() {
    if (!mapping.zone_number) { onError('You must map a column to Reporting ID'); return; }
    setImporting(true);
    try {
      const columnMap: Record<string, string> = {};
      for (const [field, col] of Object.entries(mapping)) {
        if (col) columnMap[col] = field;
      }
      const fd = new FormData();
      fd.append('file', file);
      fd.append('column_map', JSON.stringify(columnMap));
      const res = await fetch(`/api/sites/${siteId}/zones/import-csv`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('lemai_token') || ''}` },
        body: fd,
      });
      const d = await res.json();
      if (!res.ok) { onError(d.error || 'Import failed'); return; }
      onDone(d);
    } catch {
      onError('Network error — CSV import failed');
    } finally {
      setImporting(false);
    }
  }

  const labelStyle: React.CSSProperties = { fontSize: '11px', fontWeight: 600, color: '#555555', display: 'block', marginBottom: '4px', textTransform: 'uppercase' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '5px', width: '100%', maxWidth: '640px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '15px', color: '#e5e5e5' }}>Map CSV Columns</div>
            <div style={{ fontSize: '11px', color: '#555555', marginTop: '2px' }}>{file.name}</div>
          </div>
          <button onClick={onCancel} style={{ background: 'rgba(255,255,255,0.06)', border: 'none', borderRadius: '6px', color: '#555555', width: '28px', height: '28px', fontSize: '14px', cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}><Spinner size={24} /></div>
          ) : (
            <>
              {/* Column mapping */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#555555', textTransform: 'uppercase', marginBottom: '12px' }}>Tell Lemai which CSV column has each piece of info</div>
                <div style={{ display: 'grid', gap: '10px' }}>
                  {CSV_FIELDS.map(f => (
                    <div key={f.key} style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '10px', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: 700, color: f.required ? '#e2e8f0' : '#94a3b8' }}>
                          {f.label}{f.required && <span style={{ color: '#ef4444' }}> *</span>}
                        </div>
                        <div style={{ fontSize: '10px', color: '#555555', marginTop: '2px' }}>{f.hint}</div>
                      </div>
                      <select
                        value={mapping[f.key] || ''}
                        onChange={e => setMapping(p => ({ ...p, [f.key]: e.target.value }))}
                        style={{ ...selectStyle, borderColor: f.required && !mapping[f.key] ? 'rgba(239,68,68,0.5)' : undefined }}
                      >
                        <option value="">— skip —</option>
                        {headers.map(h => <option key={h} value={h}>{h}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              {/* Preview rows */}
              {preview.length > 0 && (
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#555555', textTransform: 'uppercase', marginBottom: '8px' }}>Preview (first 3 rows)</div>
                  <div style={{ overflowX: 'auto', borderRadius: '6px', border: '1px solid #1a1a1a' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                      <thead>
                        <tr>
                          {headers.map(h => {
                            const fieldFor = Object.entries(mapping).find(([, v]) => v === h)?.[0];
                            return (
                              <th key={h} style={{ padding: '6px 8px', borderBottom: '1px solid rgba(255,255,255,0.07)', textAlign: 'left', color: fieldFor ? '#60a5fa' : '#222222', fontWeight: fieldFor ? 700 : 400, whiteSpace: 'nowrap', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {fieldFor ? `${h} → ${fieldFor}` : h}
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.map((row, ri) => (
                          <tr key={ri}>
                            {row.map((cell, ci) => (
                              <td key={ci} style={{ padding: '5px 8px', borderBottom: '1px solid rgba(255,255,255,0.04)', color: '#555555', whiteSpace: 'nowrap', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={btnSecondary}>Cancel</button>
          <button
            onClick={handleImport}
            disabled={importing || loading || !mapping.zone_number}
            style={{ ...btnPrimary, opacity: importing || loading || !mapping.zone_number ? 0.5 : 1 }}
          >
            {importing ? 'Importing…' : `Import ${headers.length > 0 ? 'CSV' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── New Site Modal ───────────────────────────────────────────────────────────

interface NewSiteModalProps {
  onClose: () => void;
  onCreated: (site: Site) => void;
  onError: (msg: string) => void;
}

function NewSiteModal({ onClose, onCreated, onError }: NewSiteModalProps) {
  const [accountCode, setAccountCode] = useState('');
  const [siteName, setSiteName] = useState('');
  const [suburb, setSuburb] = useState('');
  const [state, setState] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!accountCode.trim() || !siteName.trim()) return;
    setSaving(true);
    try {
      const res = await fetch('/api/sites', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ accountCode: accountCode.trim(), siteName: siteName.trim(), suburb: suburb.trim() || undefined, state: state.trim() || undefined }),
      });
      const d = await res.json();
      if (!res.ok) { onError(d.error || 'Failed to create site'); return; }
      onCreated(d.site);
    } catch {
      onError('Network error — could not create site');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '5px', width: '100%', maxWidth: '440px' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: '15px', color: '#e5e5e5' }}>New Site</div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: 'none', borderRadius: '6px', color: '#555555', width: '28px', height: '28px', fontSize: '14px', cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <label style={{ fontSize: '11px', fontWeight: 600, color: '#555555', display: 'block', marginBottom: '5px', textTransform: 'uppercase' }}>Account Code *</label>
            <input value={accountCode} onChange={e => setAccountCode(e.target.value)} placeholder="e.g. 00012345" style={inputStyle} />
            <div style={{ fontSize: '10px', color: '#555555', marginTop: '3px' }}>8-digit account code used by alarm panel</div>
          </div>
          <div>
            <label style={{ fontSize: '11px', fontWeight: 600, color: '#555555', display: 'block', marginBottom: '5px', textTransform: 'uppercase' }}>Site Name *</label>
            <input value={siteName} onChange={e => setSiteName(e.target.value)} placeholder="e.g. Town Hall Building A" style={inputStyle} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: '10px' }}>
            <div>
              <label style={{ fontSize: '11px', fontWeight: 600, color: '#555555', display: 'block', marginBottom: '5px', textTransform: 'uppercase' }}>Suburb</label>
              <input value={suburb} onChange={e => setSuburb(e.target.value)} placeholder="e.g. Parramatta" style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: '11px', fontWeight: 600, color: '#555555', display: 'block', marginBottom: '5px', textTransform: 'uppercase' }}>State</label>
              <select value={state} onChange={e => setState(e.target.value)} style={selectStyle}>
                <option value="">—</option>
                {['NSW','VIC','QLD','WA','SA','TAS','ACT','NT'].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', paddingTop: '4px' }}>
            <button onClick={onClose} style={{ ...btnSecondary, flex: 1 }}>Cancel</button>
            <button onClick={handleSubmit} disabled={saving || !accountCode.trim() || !siteName.trim()} style={{ ...btnPrimary, flex: 2, opacity: saving || !accountCode.trim() || !siteName.trim() ? 0.5 : 1 }}>
              {saving ? 'Creating…' : 'Create Site'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Details Tab ──────────────────────────────────────────────────────────────

interface DetailsTabProps {
  site: Site;
  onSiteUpdated: (site: Site) => void;
  onError: (msg: string) => void;
  onSuccess: (msg: string) => void;
}

function DetailsTab({ site, onSiteUpdated, onError, onSuccess }: DetailsTabProps) {
  const [form, setForm] = useState({
    siteName: site.siteName,
    accountCode: site.accountCode,
    address: site.address || '',
    suburb: site.suburb || '',
    state: site.state || '',
    postcode: site.postcode || '',
    notes: site.notes || '',
    isActive: site.isActive,
  });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      siteName: site.siteName,
      accountCode: site.accountCode,
      address: site.address || '',
      suburb: site.suburb || '',
      state: site.state || '',
      postcode: site.postcode || '',
      notes: site.notes || '',
      isActive: site.isActive,
    });
    setDirty(false);
  }, [site.id]);

  function set(key: string, val: string | boolean) {
    setForm(p => ({ ...p, [key]: val }));
    setDirty(true);
  }

  async function save() {
    if (saving) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/sites/${site.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(form),
      });
      const d = await res.json();
      if (!res.ok) { onError(d.error || 'Failed to save site'); return; }
      onSiteUpdated(d.site);
      setDirty(false);
      onSuccess('Site saved');
    } catch {
      onError('Network error — could not save site');
    } finally {
      setSaving(false);
    }
  }

  const labelStyle: React.CSSProperties = { fontSize: '11px', fontWeight: 600, color: '#555555', display: 'block', marginBottom: '5px', textTransform: 'uppercase' };
  const field = (label: string, key: string, placeholder = '') => (
    <div>
      <label style={labelStyle}>{label}</label>
      <input
        value={(form as any)[key]}
        onChange={e => set(key, e.target.value)}
        onBlur={() => dirty && save()}
        placeholder={placeholder}
        style={inputStyle}
      />
    </div>
  );

  return (
    <div style={{ padding: '20px', maxWidth: '640px' }}>
      {dirty && (
        <div style={{ marginBottom: '14px', padding: '8px 12px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: '6px', fontSize: '11px', color: '#f59e0b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Unsaved changes</span>
          <button onClick={save} disabled={saving} style={{ ...btnPrimary, padding: '4px 10px', fontSize: '11px' }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      )}
      <div style={{ display: 'grid', gap: '14px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {field('Site Name *', 'siteName', 'Site name')}
          <div>
            <label style={labelStyle}>Account Code *</label>
            <input
              value={form.accountCode}
              onChange={e => set('accountCode', e.target.value)}
              onBlur={e => { set('accountCode', e.target.value.padStart(8, '0').slice(-8)); if (dirty) save(); }}
              placeholder="00000000"
              maxLength={8}
              style={{ ...inputStyle, fontFamily: 'monospace', letterSpacing: '0.08em' }}
            />
          </div>
        </div>
        {field('Address', 'address', 'Street address')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 80px', gap: '10px' }}>
          {field('Suburb', 'suburb', 'Suburb')}
          <div>
            <label style={labelStyle}>State</label>
            <select value={form.state} onChange={e => { set('state', e.target.value); }} onBlur={() => dirty && save()} style={selectStyle}>
              <option value="">—</option>
              {['NSW','VIC','QLD','WA','SA','TAS','ACT','NT'].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          {field('Postcode', 'postcode', '2000')}
        </div>
        <div>
          <label style={labelStyle}>Notes</label>
          <textarea
            value={form.notes}
            onChange={e => set('notes', e.target.value)}
            onBlur={() => dirty && save()}
            rows={4}
            placeholder="Internal notes about this site..."
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#0d0d0d', border: '1px solid #1a1a1a', borderRadius: '4px' }}>
          <Toggle value={form.isActive} onChange={v => { set('isActive', v); setTimeout(save, 0); }} />
          <div>
            <div style={{ fontSize: '13px', color: '#e5e5e5', fontWeight: 600 }}>Site Active</div>
            <div style={{ fontSize: '11px', color: '#555555' }}>Inactive sites will not receive alarm notifications</div>
          </div>
        </div>
        {!dirty && (
          <button onClick={save} disabled={saving} style={{ ...btnPrimary, alignSelf: 'flex-start' }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Zones Tab ────────────────────────────────────────────────────────────────

interface ZonesTabProps {
  site: Site;
  onSiteUpdated: (site: Site) => void;
  onError: (msg: string) => void;
  onSuccess: (msg: string) => void;
}

interface ZoneRowProps {
  zone: Zone;
  siteId: number;
  onUpdated: (z: Zone) => void;
  onDeleted: (id: number) => void;
  onError: (msg: string) => void;
  onSuccess: (msg: string) => void;
}

function ZoneRow({ zone, siteId, onUpdated, onDeleted, onError, onSuccess }: ZoneRowProps) {
  const [editing, setEditing] = useState<Partial<Zone>>({});
  const [saving, setSaving] = useState(false);

  function startEdit(field: keyof Zone) {
    if (!(field in editing)) {
      setEditing(p => ({ ...p, [field]: (zone as any)[field] }));
    }
  }

  function change(field: keyof Zone, val: any) {
    setEditing(p => ({ ...p, [field]: val }));
  }

  async function commitField(field: keyof Zone) {
    if (!(field in editing)) return;
    setSaving(true);
    try {
      const body = { [field]: editing[field] };
      const res = await fetch(`/api/sites/${siteId}/zones/${zone.id}`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) { onError(d.error || 'Failed to update zone'); return; }
      onUpdated(d.zone);
      setEditing(p => { const n = { ...p }; delete n[field]; return n; });
      onSuccess('Zone updated');
    } catch {
      onError('Network error — could not update zone');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete reporting ID ${zone.zoneNumber} — ${zone.zoneLabel || 'Unlabelled'}? This cannot be undone.`)) return;
    try {
      const res = await fetch(`/api/sites/${siteId}/zones/${zone.id}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) { const d = await res.json(); onError(d.error || 'Delete failed'); return; }
      onDeleted(zone.id);
      onSuccess('Zone deleted');
    } catch {
      onError('Network error — could not delete zone');
    }
  }

  async function toggleActive() {
    const newVal = !zone.isActive;
    setSaving(true);
    try {
      const res = await fetch(`/api/sites/${siteId}/zones/${zone.id}`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ isActive: newVal }),
      });
      const d = await res.json();
      if (!res.ok) { onError(d.error || 'Failed to update zone'); return; }
      onUpdated(d.zone);
    } catch {
      onError('Network error');
    } finally {
      setSaving(false);
    }
  }

  const tdStyle: React.CSSProperties = { padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.05)', verticalAlign: 'middle' };

  const cellInput = (field: keyof Zone, type: 'text' | 'number' = 'text') => {
    const isEditing = field in editing;
    const displayVal = isEditing ? (editing[field] as any) : (zone[field] as any) ?? '';
    if (isEditing) {
      return (
        <input
          autoFocus
          type={type}
          value={displayVal}
          onChange={e => change(field, type === 'number' ? Number(e.target.value) : e.target.value)}
          onBlur={() => commitField(field)}
          onKeyDown={e => { if (e.key === 'Enter') commitField(field); if (e.key === 'Escape') setEditing(p => { const n = { ...p }; delete n[field]; return n; }); }}
          style={{ ...inputStyle, width: '100%', minWidth: '80px' }}
        />
      );
    }
    return (
      <span onClick={() => startEdit(field)} style={{ cursor: 'text', color: displayVal ? '#e2e8f0' : '#555555', fontSize: '12px', display: 'block', padding: '2px 4px', borderRadius: '4px', minWidth: '60px' }}
        title="Click to edit">
        {displayVal || <span style={{ color: '#555555', fontStyle: 'italic' }}>—</span>}
      </span>
    );
  };

  const sev = ('severity' in editing ? editing.severity : zone.severity) as Zone['severity'];
  const typ = ('zoneType' in editing ? editing.zoneType : zone.zoneType) as Zone['zoneType'];

  return (
    <tr>
      <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '13px', color: '#a0a0a0', textAlign: 'center' }}>
        {zone.zoneNumber}
      </td>
      <td style={{ ...tdStyle, minWidth: '120px' }}>
        {cellInput('zoneLabel')}
      </td>
      <td style={{ ...tdStyle, minWidth: '100px' }}>
        {cellInput('area')}
      </td>
      <td style={tdStyle}>
        {'zoneType' in editing ? (
          <select autoFocus value={typ} onChange={e => change('zoneType', e.target.value)} onBlur={() => commitField('zoneType')}
            style={{ ...selectStyle, minWidth: '120px' }}>
            {(Object.keys(ZONE_TYPE_LABELS) as Zone['zoneType'][]).map(t => <option key={t} value={t}>{ZONE_TYPE_LABELS[t]}</option>)}
          </select>
        ) : (
          <span onClick={() => startEdit('zoneType')} style={{ cursor: 'pointer', fontSize: '12px', color: '#e5e5e5' }} title="Click to edit">
            {ZONE_TYPE_LABELS[zone.zoneType]}
          </span>
        )}
      </td>
      <td style={tdStyle}>
        {'severity' in editing ? (
          <select autoFocus value={sev} onChange={e => change('severity', e.target.value)} onBlur={() => commitField('severity')}
            style={{ ...selectStyle, minWidth: '100px', color: ZONE_SEVERITY_COLORS[editing.severity as Zone['severity'] || zone.severity] }}>
            {(['critical', 'high', 'medium', 'low'] as Zone['severity'][]).map(s => (
              <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
        ) : (
          <span onClick={() => startEdit('severity')} title="Click to edit" style={{
            cursor: 'pointer', display: 'inline-block', padding: '3px 9px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
            color: ZONE_SEVERITY_COLORS[zone.severity],
            background: `${ZONE_SEVERITY_COLORS[zone.severity]}18`,
            border: `1px solid ${ZONE_SEVERITY_COLORS[zone.severity]}40`,
          }}>
            {zone.severity.charAt(0).toUpperCase() + zone.severity.slice(1)}
          </span>
        )}
      </td>
      <td style={{ ...tdStyle, textAlign: 'center' }}>
        <Toggle value={zone.isActive} onChange={toggleActive} />
      </td>
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        {saving ? <Spinner size={14} /> : (
          <button onClick={handleDelete} style={btnDanger}>Delete</button>
        )}
      </td>
    </tr>
  );
}

interface AddZoneFormProps {
  siteId: number;
  existingZoneNumbers: number[];
  onAdded: (z: Zone) => void;
  onCancel: () => void;
  onError: (msg: string) => void;
}

function AddZoneForm({ siteId, existingZoneNumbers, onAdded, onCancel, onError }: AddZoneFormProps) {
  const [zoneNumber, setZoneNumber] = useState('');
  const [zoneLabel, setZoneLabel] = useState('');
  const [area, setArea] = useState('');
  const [zoneType, setZoneType] = useState<Zone['zoneType']>('motion');
  const [severity, setSeverity] = useState<Zone['severity']>('medium');
  const [isActive, setIsActive] = useState(true);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [dupError, setDupError] = useState(false);
  const numRef = useRef<HTMLInputElement>(null);

  useEffect(() => { numRef.current?.focus(); }, []);

  function checkDup(v: string) {
    const n = parseInt(v, 10);
    setDupError(!isNaN(n) && existingZoneNumbers.includes(n));
  }

  async function handleAdd() {
    const n = parseInt(zoneNumber, 10);
    if (isNaN(n) || n < 1) { onError('Invalid Reporting ID'); return; }
    if (existingZoneNumbers.includes(n)) { setDupError(true); return; }
    setSaving(true);
    try {
      const res = await fetch(`/api/sites/${siteId}/zones`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ zoneNumber: n, zoneLabel: zoneLabel.trim() || undefined, area: area.trim() || undefined, zoneType, severity, isActive, notes: notes.trim() || undefined }),
      });
      const d = await res.json();
      if (!res.ok) { onError(d.error || 'Failed to add zone'); return; }
      onAdded(d.zone);
    } catch {
      onError('Network error — could not add zone');
    } finally {
      setSaving(false);
    }
  }

  return (
    <tr style={{ background: 'rgba(59,130,246,0.05)' }}>
      <td style={{ padding: '10px' }}>
        <input ref={numRef} type="number" min={1} value={zoneNumber}
          onChange={e => { setZoneNumber(e.target.value); checkDup(e.target.value); }}
          placeholder="e.g. 105"
          style={{ ...inputStyle, width: '70px', borderColor: dupError ? '#ef4444' : undefined }}
        />
        {dupError && <div style={{ fontSize: '10px', color: '#ef4444', marginTop: '2px' }}>Already exists</div>}
      </td>
      <td style={{ padding: '10px' }}>
        <input value={zoneLabel} onChange={e => setZoneLabel(e.target.value)} placeholder="Zone label" style={inputStyle} />
      </td>
      <td style={{ padding: '10px' }}>
        <input value={area} onChange={e => setArea(e.target.value)} placeholder="Area / partition" style={inputStyle} />
      </td>
      <td style={{ padding: '10px' }}>
        <select value={zoneType} onChange={e => setZoneType(e.target.value as Zone['zoneType'])} style={selectStyle}>
          {(Object.keys(ZONE_TYPE_LABELS) as Zone['zoneType'][]).map(t => <option key={t} value={t}>{ZONE_TYPE_LABELS[t]}</option>)}
        </select>
      </td>
      <td style={{ padding: '10px' }}>
        <select value={severity} onChange={e => setSeverity(e.target.value as Zone['severity'])}
          style={{ ...selectStyle, color: ZONE_SEVERITY_COLORS[severity] }}>
          {(['critical', 'high', 'medium', 'low'] as Zone['severity'][]).map(s => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
      </td>
      <td style={{ padding: '10px', textAlign: 'center' }}>
        <Toggle value={isActive} onChange={setIsActive} />
      </td>
      <td style={{ padding: '10px' }}>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button onClick={handleAdd} disabled={saving || dupError || !zoneNumber} style={{ ...btnPrimary, opacity: saving || dupError || !zoneNumber ? 0.5 : 1 }}>
            {saving ? 'Adding…' : 'Add'}
          </button>
          <button onClick={onCancel} style={btnSecondary}>Cancel</button>
        </div>
      </td>
    </tr>
  );
}

function ZonesTab({ site, onSiteUpdated, onError, onSuccess }: ZonesTabProps) {
  const [zones, setZones] = useState<Zone[]>(site.zones || []);
  const [showAdd, setShowAdd] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; updated: number; skipped: number; errors: string[] } | null>(null);
  const [pendingCsvFile, setPendingCsvFile] = useState<File | null>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setZones(site.zones || []); setShowAdd(false); setImportResult(null); }, [site.id]);

  function handleCsvFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) setPendingCsvFile(file);
    e.target.value = '';
  }

  function handleImportDone(d: { imported: number; updated: number; skipped: number; errors: string[]; zones: Zone[] }) {
    setPendingCsvFile(null);
    setImportResult(d);
    if (d.zones) {
      const sorted = [...d.zones].sort((a, b) => a.zoneNumber - b.zoneNumber);
      setZones(sorted);
      onSiteUpdated({ ...site, zones: sorted });
    }
    onSuccess(`Imported ${d.imported ?? 0} new, updated ${d.updated ?? 0} zones`);
  }

  function handleUpdated(z: Zone) {
    const updated = zones.map(x => x.id === z.id ? z : x);
    setZones(updated);
    onSiteUpdated({ ...site, zones: updated });
  }

  function handleDeleted(id: number) {
    const updated = zones.filter(x => x.id !== id);
    setZones(updated);
    onSiteUpdated({ ...site, zones: updated });
  }

  function handleAdded(z: Zone) {
    const updated = [...zones, z].sort((a, b) => a.zoneNumber - b.zoneNumber);
    setZones(updated);
    onSiteUpdated({ ...site, zones: updated });
    setShowAdd(false);
    onSuccess('Zone added');
  }

  const thStyle: React.CSSProperties = {
    padding: '9px 10px', textAlign: 'left', fontSize: '10px', fontWeight: 700,
    color: '#555555', textTransform: 'uppercase', letterSpacing: '0.06em',
    borderBottom: '1px solid rgba(255,255,255,0.07)',
  };

  return (
    <>
    {pendingCsvFile && (
      <CsvMappingModal
        siteId={site.id}
        file={pendingCsvFile}
        onDone={handleImportDone}
        onCancel={() => setPendingCsvFile(null)}
        onError={msg => { onError(msg); setPendingCsvFile(null); }}
      />
    )}
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
        <div style={{ fontSize: '13px', color: '#a0a0a0' }}>
          {zones.length} zone{zones.length !== 1 ? 's' : ''} programmed — click any cell to edit inline
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {importResult && (
            <span style={{ fontSize: '11px', color: '#4ade80' }}>
              +{importResult.imported} new, ~{importResult.updated} updated{importResult.errors.length > 0 ? `, ${importResult.errors.length} errors` : ''}
            </span>
          )}
          <input ref={csvInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleCsvFileSelected} />
          <button
            onClick={() => csvInputRef.current?.click()}
            style={btnSecondary}
            title="Import zones from a CSV export — you'll choose which columns to use"
          >
            Import CSV
          </button>
          {!showAdd && (
            <button onClick={() => setShowAdd(true)} style={btnPrimary}>+ Add Zone</button>
          )}
        </div>
      </div>
      <div style={{ overflowX: 'auto', borderRadius: '4px', border: '1px solid #1a1a1a' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '560px' }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, width: '80px', textAlign: 'center' }}>Report ID</th>
              <th style={thStyle}>Label</th>
              <th style={thStyle}>Area</th>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>Severity</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Active</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {zones.length === 0 && !showAdd && (
              <tr>
                <td colSpan={7} style={{ padding: '32px', textAlign: 'center', color: '#555555', fontSize: '13px' }}>
                  No zones programmed. Click "Add Zone" to add the first zone.
                </td>
              </tr>
            )}
            {zones.map(z => (
              <ZoneRow key={z.id} zone={z} siteId={site.id} onUpdated={handleUpdated} onDeleted={handleDeleted} onError={onError} onSuccess={onSuccess} />
            ))}
            {showAdd && (
              <AddZoneForm
                siteId={site.id}
                existingZoneNumbers={zones.map(z => z.zoneNumber)}
                onAdded={handleAdded}
                onCancel={() => setShowAdd(false)}
                onError={onError}
              />
            )}
          </tbody>
        </table>
      </div>
    </div>
    </>
  );
}

// ─── Contacts Tab ─────────────────────────────────────────────────────────────

interface ContactsTabProps {
  site: Site;
  onSiteUpdated: (site: Site) => void;
  onError: (msg: string) => void;
  onSuccess: (msg: string) => void;
}

interface ContactCardProps {
  contact: Contact;
  siteId: number;
  onUpdated: (c: Contact) => void;
  onDeleted: (id: number) => void;
  onError: (msg: string) => void;
  onSuccess: (msg: string) => void;
}

function ContactCard({ contact, siteId, onUpdated, onDeleted, onError, onSuccess }: ContactCardProps) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: contact.name, role: contact.role, phone: contact.phone || '', email: contact.email || '', priorityOrder: contact.priorityOrder, isActive: contact.isActive, notes: contact.notes || '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({ name: contact.name, role: contact.role, phone: contact.phone || '', email: contact.email || '', priorityOrder: contact.priorityOrder, isActive: contact.isActive, notes: contact.notes || '' });
  }, [contact]);

  async function handleSave() {
    setSaving(true);
    try {
      const res = await fetch(`/api/sites/${siteId}/contacts/${contact.id}`, {
        method: 'PUT', headers: authHeaders(),
        body: JSON.stringify({ ...form, phone: form.phone || undefined, email: form.email || undefined, notes: form.notes || undefined }),
      });
      const d = await res.json();
      if (!res.ok) { onError(d.error || 'Failed to update contact'); return; }
      onUpdated(d.contact);
      setEditing(false);
      onSuccess('Contact updated');
    } catch {
      onError('Network error — could not update contact');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete contact "${contact.name}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`/api/sites/${siteId}/contacts/${contact.id}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) { const d = await res.json(); onError(d.error || 'Delete failed'); return; }
      onDeleted(contact.id);
      onSuccess('Contact deleted');
    } catch {
      onError('Network error — could not delete contact');
    }
  }

  async function toggleActive() {
    try {
      const res = await fetch(`/api/sites/${siteId}/contacts/${contact.id}`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ isActive: !contact.isActive }),
      });
      const d = await res.json();
      if (!res.ok) { onError(d.error || 'Failed to update contact'); return; }
      onUpdated(d.contact);
    } catch {
      onError('Network error');
    }
  }

  const labelStyle: React.CSSProperties = { fontSize: '10px', fontWeight: 700, color: '#555555', display: 'block', marginBottom: '4px', textTransform: 'uppercase' };

  if (editing) {
    return (
      <div style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '4px', padding: '14px' }}>
        <div style={{ display: 'grid', gap: '10px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <label style={labelStyle}>Name *</label>
              <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Role</label>
              <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value as Contact['role'] }))} style={selectStyle}>
                {(Object.keys(CONTACT_ROLE_LABELS) as Contact['role'][]).map(r => <option key={r} value={r}>{CONTACT_ROLE_LABELS[r]}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <label style={labelStyle}>Phone</label>
              <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="04xx xxx xxx" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="name@example.com" style={inputStyle} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '10px' }}>
            <div>
              <label style={labelStyle}>Priority</label>
              <input type="number" min={1} max={99} value={form.priorityOrder} onChange={e => setForm(p => ({ ...p, priorityOrder: parseInt(e.target.value) || 1 }))} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Notes</label>
              <input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional notes" style={inputStyle} />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Toggle value={form.isActive} onChange={v => setForm(p => ({ ...p, isActive: v }))} />
            <span style={{ fontSize: '12px', color: '#a0a0a0' }}>Active</span>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handleSave} disabled={saving || !form.name.trim()} style={{ ...btnPrimary, opacity: saving || !form.name.trim() ? 0.5 : 1 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => setEditing(false)} style={btnSecondary}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: '#0d0d0d', border: '1px solid #1a1a1a',
      borderRadius: '4px', padding: '14px', opacity: contact.isActive ? 1 : 0.6,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        {/* Priority badge */}
        <div style={{
          width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
          background: `rgba(59,130,246,0.15)`, border: '1px solid rgba(59,130,246,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '13px', fontWeight: 700, color: '#60a5fa',
        }}>
          {contact.priorityOrder}
        </div>
        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#e5e5e5' }}>{contact.name}</span>
            <span style={{
              padding: '2px 8px', borderRadius: '20px', fontSize: '10px', fontWeight: 700,
              color: CONTACT_ROLE_COLORS[contact.role],
              background: `${CONTACT_ROLE_COLORS[contact.role]}18`,
              border: `1px solid ${CONTACT_ROLE_COLORS[contact.role]}35`,
            }}>
              {CONTACT_ROLE_LABELS[contact.role]}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
            {contact.phone && <span style={{ fontSize: '12px', color: '#a0a0a0' }}>📞 {contact.phone}</span>}
            {contact.email && <span style={{ fontSize: '12px', color: '#a0a0a0' }}>✉ {contact.email}</span>}
            {!contact.phone && !contact.email && <span style={{ fontSize: '11px', color: '#555555', fontStyle: 'italic' }}>No contact details</span>}
          </div>
          {contact.notes && <div style={{ fontSize: '11px', color: '#555555', marginTop: '4px' }}>{contact.notes}</div>}
        </div>
        {/* Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <Toggle value={contact.isActive} onChange={toggleActive} />
          <button onClick={() => setEditing(true)} style={btnSecondary}>Edit</button>
          <button onClick={handleDelete} style={btnDanger}>Delete</button>
        </div>
      </div>
    </div>
  );
}

interface AddContactFormProps {
  siteId: number;
  nextPriority: number;
  onAdded: (c: Contact) => void;
  onCancel: () => void;
  onError: (msg: string) => void;
}

function AddContactForm({ siteId, nextPriority, onAdded, onCancel, onError }: AddContactFormProps) {
  const [form, setForm] = useState({ name: '', role: 'keyholder' as Contact['role'], phone: '', email: '', priorityOrder: nextPriority, isActive: true, notes: '' });
  const [saving, setSaving] = useState(false);

  async function handleAdd() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/sites/${siteId}/contacts`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ name: form.name.trim(), role: form.role, phone: form.phone || undefined, email: form.email || undefined, priorityOrder: form.priorityOrder, isActive: form.isActive, notes: form.notes || undefined }),
      });
      const d = await res.json();
      if (!res.ok) { onError(d.error || 'Failed to add contact'); return; }
      onAdded(d.contact);
    } catch {
      onError('Network error — could not add contact');
    } finally {
      setSaving(false);
    }
  }

  const labelStyle: React.CSSProperties = { fontSize: '10px', fontWeight: 700, color: '#555555', display: 'block', marginBottom: '4px', textTransform: 'uppercase' };

  return (
    <div style={{ background: 'rgba(34,197,94,0.05)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: '4px', padding: '14px', marginTop: '10px' }}>
      <div style={{ fontSize: '12px', fontWeight: 700, color: '#4ade80', marginBottom: '12px' }}>New Contact</div>
      <div style={{ display: 'grid', gap: '10px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <div>
            <label style={labelStyle}>Name *</label>
            <input autoFocus value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Full name" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Role</label>
            <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value as Contact['role'] }))} style={selectStyle}>
              {(Object.keys(CONTACT_ROLE_LABELS) as Contact['role'][]).map(r => <option key={r} value={r}>{CONTACT_ROLE_LABELS[r]}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <div>
            <label style={labelStyle}>Phone</label>
            <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="04xx xxx xxx" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Email</label>
            <input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="name@example.com" style={inputStyle} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '10px' }}>
          <div>
            <label style={labelStyle}>Priority</label>
            <input type="number" min={1} max={99} value={form.priorityOrder} onChange={e => setForm(p => ({ ...p, priorityOrder: parseInt(e.target.value) || 1 }))} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Notes</label>
            <input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional" style={inputStyle} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Toggle value={form.isActive} onChange={v => setForm(p => ({ ...p, isActive: v }))} />
          <span style={{ fontSize: '12px', color: '#a0a0a0' }}>Active</span>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={handleAdd} disabled={saving || !form.name.trim()} style={{ ...btnPrimary, opacity: saving || !form.name.trim() ? 0.5 : 1 }}>
            {saving ? 'Adding…' : 'Add Contact'}
          </button>
          <button onClick={onCancel} style={btnSecondary}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function ContactsTab({ site, onSiteUpdated, onError, onSuccess }: ContactsTabProps) {
  const [contacts, setContacts] = useState<Contact[]>([...(site.contacts || [])].sort((a, b) => a.priorityOrder - b.priorityOrder));
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    setContacts([...(site.contacts || [])].sort((a, b) => a.priorityOrder - b.priorityOrder));
    setShowAdd(false);
  }, [site.id]);

  function handleUpdated(c: Contact) {
    const updated = contacts.map(x => x.id === c.id ? c : x).sort((a, b) => a.priorityOrder - b.priorityOrder);
    setContacts(updated);
    onSiteUpdated({ ...site, contacts: updated });
  }

  function handleDeleted(id: number) {
    const updated = contacts.filter(x => x.id !== id);
    setContacts(updated);
    onSiteUpdated({ ...site, contacts: updated });
  }

  function handleAdded(c: Contact) {
    const updated = [...contacts, c].sort((a, b) => a.priorityOrder - b.priorityOrder);
    setContacts(updated);
    onSiteUpdated({ ...site, contacts: updated });
    setShowAdd(false);
    onSuccess('Contact added');
  }

  const nextPriority = contacts.length > 0 ? Math.max(...contacts.map(c => c.priorityOrder)) + 1 : 1;

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: '13px', color: '#a0a0a0' }}>
          {contacts.length} contact{contacts.length !== 1 ? 's' : ''} — sorted by call priority order
        </div>
        {!showAdd && (
          <button onClick={() => setShowAdd(true)} style={btnPrimary}>+ Add Contact</button>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {contacts.length === 0 && !showAdd && (
          <div style={{ padding: '32px', textAlign: 'center', color: '#555555', fontSize: '13px', background: '#0d0d0d', borderRadius: '4px', border: '1px solid #1a1a1a' }}>
            No contacts programmed. Add keyholders and emergency contacts.
          </div>
        )}
        {contacts.map(c => (
          <ContactCard key={c.id} contact={c} siteId={site.id} onUpdated={handleUpdated} onDeleted={handleDeleted} onError={onError} onSuccess={onSuccess} />
        ))}
        {showAdd && (
          <AddContactForm siteId={site.id} nextPriority={nextPriority} onAdded={handleAdded} onCancel={() => setShowAdd(false)} onError={onError} />
        )}
      </div>
    </div>
  );
}

// ─── Site Detail Panel ────────────────────────────────────────────────────────

interface SiteDetailProps {
  siteId: number;
  onSiteUpdated: (site: Site) => void;
  onError: (msg: string) => void;
  onSuccess: (msg: string) => void;
}

function SiteDetail({ siteId, onSiteUpdated, onError, onSuccess }: SiteDetailProps) {
  const [site, setSite] = useState<Site | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'details' | 'zones' | 'contacts'>('details');

  const loadSite = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/sites/${siteId}`, { headers: authHeaders() });
      const d = await res.json();
      if (!res.ok) { onError(d.error || 'Failed to load site'); return; }
      setSite(d.site);
    } catch {
      onError('Network error — could not load site');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => { loadSite(); setTab('details'); }, [siteId]);

  function handleSiteUpdated(updated: Site) {
    setSite(updated);
    onSiteUpdated(updated);
  }

  const tabStyle = (t: string): React.CSSProperties => ({
    padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer',
    fontSize: '13px', fontWeight: 600,
    color: tab === t ? '#e2e8f0' : '#555555',
    borderBottom: tab === t ? '2px solid #3b82f6' : '2px solid transparent',
    transition: 'all 0.15s',
  });

  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Spinner size={28} />
      </div>
    );
  }

  if (!site) return null;

  const zoneCount = site.zones?.length ?? 0;
  const contactCount = site.contacts?.length ?? 0;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', background: '#0d0d0d' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '16px', fontWeight: 700, color: '#e5e5e5' }}>{site.siteName}</span>
              <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#555555', background: 'rgba(255,255,255,0.05)', padding: '2px 8px', borderRadius: '4px' }}>
                {site.accountCode.padStart(8, '0')}
              </span>
              <span style={{
                padding: '3px 9px', borderRadius: '20px', fontSize: '10px', fontWeight: 700,
                color: site.isActive ? '#22c55e' : '#555555',
                background: site.isActive ? 'rgba(34,197,94,0.12)' : 'rgba(100,116,139,0.12)',
                border: `1px solid ${site.isActive ? 'rgba(34,197,94,0.3)' : 'rgba(100,116,139,0.3)'}`,
              }}>
                {site.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            {(site.suburb || site.state) && (
              <div style={{ fontSize: '12px', color: '#555555', marginTop: '3px' }}>
                {[site.address, site.suburb, site.state, site.postcode].filter(Boolean).join(', ')}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.07)', paddingLeft: '8px' }}>
        <button style={tabStyle('details')} onClick={() => setTab('details')}>Details</button>
        <button style={tabStyle('zones')} onClick={() => setTab('zones')}>
          Zones {zoneCount > 0 && <span style={{ marginLeft: '5px', padding: '1px 7px', borderRadius: '4px', fontSize: '10px', background: 'rgba(59,130,246,0.15)', color: '#60a5fa' }}>{zoneCount}</span>}
        </button>
        <button style={tabStyle('contacts')} onClick={() => setTab('contacts')}>
          Contacts {contactCount > 0 && <span style={{ marginLeft: '5px', padding: '1px 7px', borderRadius: '4px', fontSize: '10px', background: 'rgba(34,197,94,0.12)', color: '#4ade80' }}>{contactCount}</span>}
        </button>
      </div>
      {/* Tab content */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {tab === 'details' && <DetailsTab site={site} onSiteUpdated={handleSiteUpdated} onError={onError} onSuccess={onSuccess} />}
        {tab === 'zones' && <ZonesTab site={site} onSiteUpdated={handleSiteUpdated} onError={onError} onSuccess={onSuccess} />}
        {tab === 'contacts' && <ContactsTab site={site} onSiteUpdated={handleSiteUpdated} onError={onError} onSuccess={onSuccess} />}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SitesProgramming() {
  const [sites, setSites] = useState<Site[]>([]);
  const [loadingSites, setLoadingSites] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [showNewSite, setShowNewSite] = useState(false);
  const [toasts, setToasts] = useState<ToastMsg[]>([]);
  const toastSeq = useRef(0);

  function showToast(msg: string, type: ToastMsg['type'] = 'info') {
    const id = ++toastSeq.current;
    setToasts(p => [...p, { id, msg, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 3500);
  }

  function dismissToast(id: number) { setToasts(p => p.filter(t => t.id !== id)); }
  function showError(msg: string) { showToast(msg, 'error'); }
  function showSuccess(msg: string) { showToast(msg, 'success'); }

  const loadSites = useCallback(async () => {
    setLoadingSites(true);
    try {
      const res = await fetch('/api/sites', { headers: authHeaders() });
      const d = await res.json();
      if (!res.ok) { showError(d.error || 'Failed to load sites'); return; }
      setSites(d.sites || []);
    } catch {
      showError('Network error — could not load sites');
    } finally {
      setLoadingSites(false);
    }
  }, []);

  useEffect(() => { loadSites(); }, []);

  function handleSiteCreated(site: Site) {
    setSites(p => [site, ...p]);
    setSelectedId(site.id);
    setShowNewSite(false);
    showSuccess(`Site "${site.siteName}" created`);
  }

  function handleSiteUpdated(updated: Site) {
    setSites(p => p.map(s => s.id === updated.id ? { ...s, siteName: updated.siteName, accountCode: updated.accountCode, suburb: updated.suburb, state: updated.state, isActive: updated.isActive } : s));
  }

  const filteredSites = sites.filter(s => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      s.siteName.toLowerCase().includes(q) ||
      s.accountCode.toLowerCase().includes(q) ||
      (s.suburb || '').toLowerCase().includes(q) ||
      (s.address || '').toLowerCase().includes(q)
    );
  });

  return (
    <Layout title="Sites Programming" subtitle="Customer site database — account codes, zone labels and keyholders">
      <style>{`@keyframes sp-spin { to { transform: rotate(360deg); } }`}</style>
      <ToastArea toasts={toasts} onDismiss={dismissToast} />
      {showNewSite && (
        <NewSiteModal
          onClose={() => setShowNewSite(false)}
          onCreated={handleSiteCreated}
          onError={showError}
        />
      )}

      {/* Two-panel layout */}
      <div style={{ display: 'flex', gap: '0', height: 'calc(100vh - 130px)', minHeight: '500px', background: 'rgba(255,255,255,0.01)', border: '1px solid #1a1a1a', borderRadius: '4px', overflow: 'hidden' }}>

        {/* ── Left panel: site list ── */}
        <div style={{ width: '320px', flexShrink: 0, borderRight: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', background: 'rgba(255,255,255,0.01)' }}>
          {/* Search + New */}
          <div style={{ padding: '12px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', gap: '8px' }}>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search sites..."
              style={{ ...inputStyle, flex: 1 }}
            />
            <button onClick={() => setShowNewSite(true)} style={{ ...btnPrimary, padding: '8px 12px', whiteSpace: 'nowrap', flexShrink: 0 }}>+ New</button>
          </div>

          {/* Count */}
          <div style={{ padding: '8px 12px 4px', fontSize: '10px', fontWeight: 700, color: '#555555', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {loadingSites ? 'Loading…' : `${filteredSites.length} of ${sites.length} sites`}
          </div>

          {/* List */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loadingSites ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} style={{ padding: '12px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <div style={{ height: '12px', width: '70%', background: 'rgba(255,255,255,0.05)', borderRadius: '4px', marginBottom: '6px' }} />
                    <div style={{ height: '10px', width: '40%', background: '#111111', borderRadius: '4px' }} />
                  </div>
                ))}
              </div>
            ) : filteredSites.length === 0 ? (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: '#555555', fontSize: '12px' }}>
                {sites.length === 0 ? 'No sites yet. Click "+ New" to add the first site.' : 'No sites match your search.'}
              </div>
            ) : (
              filteredSites.map(s => {
                const isSelected = s.id === selectedId;
                return (
                  <div
                    key={s.id}
                    onClick={() => setSelectedId(s.id)}
                    style={{
                      padding: '11px 12px 11px 14px',
                      cursor: 'pointer',
                      borderBottom: '1px solid rgba(255,255,255,0.04)',
                      borderLeft: isSelected ? '3px solid #3b82f6' : '3px solid transparent',
                      background: isSelected ? 'rgba(59,130,246,0.08)' : 'transparent',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: isSelected ? '#e2e8f0' : '#cbd5e1', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s.siteName}
                        </div>
                        <div style={{ fontSize: '11px', color: '#555555', fontFamily: 'monospace' }}>
                          {s.accountCode.padStart(8, '0')}
                          {s.suburb && <span style={{ fontFamily: 'inherit', marginLeft: '6px', color: '#3d4f66' }}>· {s.suburb}</span>}
                        </div>
                      </div>
                      {/* Active dot */}
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: s.isActive ? '#22c55e' : '#222222', flexShrink: 0, marginTop: '4px' }} title={s.isActive ? 'Active' : 'Inactive'} />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* ── Right panel: detail ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {selectedId ? (
            <SiteDetail
              key={selectedId}
              siteId={selectedId}
              onSiteUpdated={handleSiteUpdated}
              onError={showError}
              onSuccess={showSuccess}
            />
          ) : (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '14px', color: '#555555' }}>
              <div style={{ fontSize: '48px', opacity: 0.3 }}>🏢</div>
              <div style={{ fontSize: '15px', fontWeight: 600, color: '#444444' }}>Select a site or add a new one</div>
              <div style={{ fontSize: '12px', color: '#555555', textAlign: 'center', maxWidth: '280px', lineHeight: '1.6' }}>
                Select a site from the list to view and edit its details, zones, and contact keyholders.
              </div>
              <button onClick={() => setShowNewSite(true)} style={btnPrimary}>+ New Site</button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
